import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import '../src/assets/css/styles.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { RouterProvider } from 'react-router-dom';
import { CssBaseline, ThemeProvider, CircularProgress, Box } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { theme } from './theme/theme';
import BreakpointsProvider from './providers/BreakpointsProvider';
import { store, persistor } from './Redux/store/store';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import router from './AppRoutes/router';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_AUTH_ID } from './config';
import { ToastProvider } from './context/ToastContext';
import FullScreenLoader from './components/FullScreenLoader';
import { SnackbarProvider } from './context/SnackbarContext';
import { PlatformTimezoneProvider } from './context/PlatformTimezoneContext';

// Error Boundary Component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <h1>Something went wrong. Please refresh the page.</h1>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Loading fallback
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);

// Preload critical resources
const preloadResources = () => {
  // Add preload for critical assets here
  // Example: document.createElement('link').rel = 'preload';
};

// Initialize app
const root = ReactDOM.createRoot(document.getElementById('root')!);

// Preload resources before rendering
preloadResources();

root.render(
  <ErrorBoundary>
    <GoogleOAuthProvider clientId={GOOGLE_AUTH_ID}>
      <Provider store={store}>
        <PersistGate loading={<FullScreenLoader visible={true} />} persistor={persistor}>
          <ThemeProvider theme={theme}>
            <BreakpointsProvider>
              <CssBaseline />
              <SnackbarProvider>
                <ToastProvider>
                  <PlatformTimezoneProvider>
                    <Suspense fallback={<FullScreenLoader visible={true} />}>
                      <RouterProvider router={router} />
                    </Suspense>
                    <Toaster position="top-right" reverseOrder={false} />
                  </PlatformTimezoneProvider>
                </ToastProvider>
              </SnackbarProvider>
            </BreakpointsProvider>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </GoogleOAuthProvider>
  </ErrorBoundary>,
);
